import { Brick, ErrorFlow, registerBrick, Transaction, BrickContext, Query } from 'olympe';
import Unit from '../../../models/Unit';
import Observation from '../../../models/Observation';
import Inspection from "../../../models/Inspection";
import Component from "../../../models/Component";
import System from "../../../models/System";
import {getInspectionEntityChildren} from "./CreateMissingObservations";

export default class DeleteObservations extends Brick {
    /**
     * @override
     * @protected
     * @param {!BrickContext} $
     * @param {!Unit} unit
     * @param {!Array<InspectionEntity>} inspectionEntities
     * @param {!boolean} onlyAvailable
     * @param {function()} forwardEvent
     * @param {function(*)} setErrorFlow
     */
    update($, [unit, inspectionEntities = []], [forwardEvent, setErrorFlow]) {
        if (!(unit instanceof Unit)) {
            setErrorFlow(ErrorFlow.create(`Try to delete observations for a Unit which is not a Unit object: ${unit}`, 1));
            return;
        }

        const observations = new Map(Query.from(unit)
            .follow(Unit.observationsRel).andReturn()
            .follow(Observation.targetRel).andReturn()
            .executeFromCache()
            .map(([obs, entity]) => [entity.getTag(), obs])
        );

        const transaction = new Transaction(true);

        const entitiesToClean = new Set(inspectionEntities.flatMap((entity) => {
            return getInspectionEntityChildren(entity, false, null).map((entity) => entity.getTag());
        }));

        inspectionEntities.forEach((entity) => {
            this.findParentsToClean(entity, entitiesToClean, observations);
        });

        entitiesToClean.forEach((entity) => {
            if (observations.has(entity)) {
                transaction.delete(observations.get(entity));
            }
        });

        transaction.execute().then(forwardEvent).catch((e) => {
            setErrorFlow(ErrorFlow.create(`Failed to create observations: ${e}`, 0));
        });
    }

    /**
     * @private
     * @param {!InspectionEntity} entity
     * @param {!Set<string>} toBeCleaned
     * @param {!Map<string, Observation>} entitiesWithObservation
     */
    findParentsToClean(entity, toBeCleaned, entitiesWithObservation) {
        let parent, siblings;
        if (entity instanceof Inspection) {
            parent = entity.followSingle(Component.inspectionsRel.getInverse()).executeFromCache();
            siblings = parent.follow(Component.inspectionsRel).executeFromCache()
                .filter((entity) => entitiesWithObservation.has(entity.getTag()));
        }

        else if (entity instanceof Component) {
            parent = entity.followSingle(Component.subComponentsRel.getInverse()).executeFromCache();
            if (parent) {
                siblings = parent.follow(Component.subComponentsRel).executeFromCache()
                    .filter((entity) => entitiesWithObservation.has(entity.getTag()));
            } else {
                parent = entity.followSingle(System.componentsRel.getInverse()).executeFromCache();
                siblings = parent.follow(System.componentsRel).executeFromCache()
                    .filter((entity) => entitiesWithObservation.has(entity.getTag()));
            }
        }

        // All children of the parent will be clean, so clean the parent and recurse.
        if (parent && siblings?.every((sibling) => toBeCleaned.has(sibling.getTag()))) {
            toBeCleaned.add(parent.getTag());
            this.findParentsToClean(parent, toBeCleaned, entitiesWithObservation);
        }
    }
}

registerBrick('018973d91853228b52e5', DeleteObservations);
